import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import PlusIcon from '@material-ui/icons/Add';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { default as CloseIcon } from '@material-ui/icons/Close';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import classNames from 'classnames';
import { customAlphabet } from 'nanoid/non-secure';
import React, { useEffect, useState } from 'react';
import FaroParameterListInput from '../../../faro/parameters/FaroParameterListInput';
import ActionParamSetListInput from './ActionParamSetListInput';

const GENERATED_ACTION_NAME_REGEX = new RegExp(`^-\\w{5}$`);

const nanoid = customAlphabet('_0123456789abcdefghijklmnopqrstuvwxyz', 5);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      marginBottom: 16,
      padding: '4px 8px 4px 8px',
      backgroundColor: darken(theme.palette.background.default, 0.03)
    },
    section: {
      marginBottom: 32
    },
    dialogTitle: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    closeButton: {
      padding: 4
    },
    sectionContent: {
      padding: 8
    },
    textField: {
      marginBottom: 16
    },
    deckIdentifierToggleButton: {
      height: 24
    },
    autocompleteCheckbox: {
      marginRight: 8
    },
    parameterSetSectionTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    addButton: {
      textTransform: 'none'
    },
    parametersTableHead: {
      backgroundColor: 'inherit'
    }
  })
);

const CF_CAPABILITY_FLAGS = [
  'CAPABILITY_IAM',
  'CAPABILITY_NAMED_IAM',
  'CAPABILITY_AUTO_EXPAND'
];

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface Props {
  action: any;
  accountId: string;
  region: string;
  deploymentActions: any[];
  testActions: any[];
  onClose: () => void;
  onChange: (action: any) => void;
}

const FARO_OPTION_LABELS = {
  update: 'Update Existing Deployment',
  global: 'Global Deployment',
  skipStacks: 'Skip Stacks',
  disableStackRollback: 'Disable Stack Rollback',
  ignoreExistingParameters: 'Ignore Existing Parameters'
};

function getFaroOptions(action) {
  const options = [];
  if (action.Update) {
    options.push('update');
  }
  if (action.Global) {
    options.push('global');
  }
  if (action.SkipStacks) {
    options.push('skipStacks');
  }
  if (action.DisableStackRollback) {
    options.push('disableStackRollback');
  }
  if (action.IgnoreExistingParameters) {
    options.push('ignoreExistingParameters');
  }
  return options;
}

function DeploymentActionDialog(props: Props) {
  const { action, accountId, region, deploymentActions, testActions, onClose, onChange } = props;
  const classes = useStyles({});

  const [actionName, setActionName] = useState(action.ActionName);
  const [actionGroup, setActionGroup] = useState(action.ActionGroup);
  const [autogenerateActionName, setAutogenerateActionName] = useState(!action.DeckName || (action.ActionName.startsWith(`${action.DeckName}`) && GENERATED_ACTION_NAME_REGEX.test(action.ActionName.substring(action.DeckName.length))));
  const actionNameSlug = useState(autogenerateActionName && action.DeckName ? action.ActionName.substring(action.ActionName.length - 5) : nanoid())[0];
  const [deckName, setDeckName] = useState(action.DeckName);
  const [deckIdentifier, setDeckIdentifier] = useState(action.Tag || action.Version || '');
  const [deckIdentifierType, setDeckIdentifierType] = useState(action.Version ? 'version' : 'tag');
  const [deploymentName, setDeploymentName] = useState(action.DeploymentName);
  const [capabilityFlags, setCapabilityFlags] = useState(action.CapabilityFlags && action.CapabilityFlags.length ? action.CapabilityFlags : (action.IAM ? ['CAPABILITY_NAMED_IAM'] : []));
  const [faroOptions, setFaroOptions] = useState(getFaroOptions(action));
  const [assumeRoleArn, setAssumeRoleArn] = useState(action.AssumeRoleARN);
  const [teardownAssumeRoleArn, setTeardownAssumeRoleArn] = useState(action.TeardownAssumeRoleARN);
  const [dependencies, setDependencies] = useState(action.Dependencies || [])
  const [parameters, setParameters] = useState(Object.keys(action.Parameters).length > 0 ? Object.keys(action.Parameters).map(key => ({ Name: key, Value: action.Parameters[key] })) : []);
  const [paramSets, setParamSets] = useState(action.ParameterSets ? action.ParameterSets.map(paramSet => ({ name: paramSet.Name, version: paramSet.Version })) : []);
  const [showParameters, setShowParameters] = useState(parameters.length > 0);
  const [showErrors, setShowErrors] = useState(false);
  const [actionNameSet, setActionNameSet] = useState(new Set());

  useEffect(() => {
    setActionNameSet([...testActions, ...deploymentActions].reduce((accumulator, item) => {
      accumulator.add(item.ActionName);
      return accumulator;
    }, new Set()));
  }, [deploymentActions, testActions]);

  const onUpdate = () => {
    // Check for required fields
    if (!actionName || !deckName || !deckIdentifier || !deploymentName || !actionName || actionNameSet.has(actionName)) {
      setShowErrors(true);
    } else {
      onChange({
        ActionName: actionName,
        DeckName: deckName,
        [deckIdentifierType === 'version' ? 'Version' : 'Tag']: deckIdentifier,
        DeploymentName: deploymentName,
        CapabilityFlags: capabilityFlags,
        IgnoreExistingParameters: faroOptions.includes('ignoreExistingParameters'),
        DisableStackRollback: faroOptions.includes('disableStackRollback'),
        Update: faroOptions.includes('update'),
        Global: faroOptions.includes('global'),
        SkipStacks: faroOptions.includes('skipStacks'),
        ActionGroup: actionGroup,
        AssumeRoleARN: assumeRoleArn,
        TeardownAssumeRoleARN: teardownAssumeRoleArn,
        Dependencies: dependencies,
        ParameterSets: paramSets.map(paramSet => ({ Name: paramSet.name, Version: paramSet.version })),
        Parameters: parameters.reduce((accumulator, param) => {
          accumulator[param.Name] = param.Value;
          return accumulator;
        }, {})
      });
    }
  };

  const onDeckIdentifierChange = (event, value) => {
    setDeckIdentifierType(value);
  };

  const onCapabilityFlagsChange = (event: React.ChangeEvent<{}>, value: any) => {
    setCapabilityFlags(value);
  };

  const onAddParamSet = () => {
    const paramSetsCopy = paramSets.slice();
    paramSetsCopy.push({ name: '', version: '' });
    setParamSets(paramSetsCopy);
  };

  const onDeploymentNameChange = (event) => {
    setDeploymentName(event.target.value);
  };

  const onDependenciesChange = (event: React.ChangeEvent<{}>, value: any) => {
    setDependencies(value);
  };

  const onFaroOptionsChange = (event: React.ChangeEvent<{}>, value: any) => {
    setFaroOptions(value);
  };

  const onDeckNameChange = (event) => {
    const newDeckName = event.target.value;
    setDeckName(newDeckName);
    if (autogenerateActionName) {
      setActionName(newDeckName ? `${event.target.value}-${actionNameSlug}` : '');
    }
  };

  const onAutogenerateActionNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAutogenerateActionName(event.target.checked);
    if (event.target.checked) {
      setActionName(deckName ? `${deckName}-${actionNameSlug}` : '');
    }
  };

  return (
    <Dialog
      open={true}
      scroll="paper"
      onClose={onClose}
      aria-labelledby="deployment-action-dialog-title"
      aria-describedby="deployment-action-dialog-description"
      maxWidth="md"
      fullWidth
      disableBackdropClick
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6" color="primary">Deployment Action</Typography>
        <IconButton onClick={onClose} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form noValidate autoComplete="off">

          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.title}>Deck Details</Typography>
            <div className={classes.sectionContent}>
              <TextField
                label="Deck Name"
                fullWidth
                required
                error={showErrors && !deckName}
                helperText={showErrors && !deckName ? 'Deck name is required' : ''}
                value={deckName}
                onChange={onDeckNameChange}
                className={classes.textField}
                InputLabelProps={{
                  shrink: true
                }} />
              <TextField
                required
                error={showErrors && !deckIdentifier}
                helperText={showErrors && !deckIdentifier ? 'Deck identifier is required' : ''}
                fullWidth
                label="Deck Identifier"
                value={deckIdentifier}
                onChange={(event) => setDeckIdentifier(event.target.value)}
                InputLabelProps={{
                  shrink: true
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">
                    <ToggleButtonGroup size="small" value={deckIdentifierType} exclusive onChange={onDeckIdentifierChange}>
                      <ToggleButton value="tag" className={classes.deckIdentifierToggleButton}>
                        Tag
                      </ToggleButton>
                      <ToggleButton value="version" className={classes.deckIdentifierToggleButton}>
                        Version
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </InputAdornment>
                }}
              />
            </div>
          </div>
          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.title}>Deployment Details</Typography>
            <div className={classes.sectionContent}>
              <TextField
                fullWidth
                required
                error={showErrors && !deploymentName}
                helperText={showErrors && !deploymentName ? 'Deployment name is required' : ''}
                value={deploymentName}
                onChange={onDeploymentNameChange}
                label="Deployment Name"
                className={classes.textField}
                InputLabelProps={{
                  shrink: true
                }} />

              <Autocomplete
                value={capabilityFlags}
                onChange={onCapabilityFlagsChange}
                options={CF_CAPABILITY_FLAGS}
                getOptionLabel={(option) => option}
                multiple
                size="small"
                className={classes.textField}
                renderOption={(option, { selected }) => (
                  <React.Fragment>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      className={classes.autocompleteCheckbox}
                      checked={selected}
                    />
                    {option}
                  </React.Fragment>
                )}
                renderInput={(params) => <TextField {...params} label="CloudFormation Capability Flags" margin="none" InputLabelProps={{
                  shrink: true
                }} />}
              />

              <Autocomplete
                value={faroOptions}
                onChange={onFaroOptionsChange}
                options={['update', 'global', 'skipStacks', 'disableStackRollback', 'ignoreExistingParameters']}
                getOptionLabel={(option) => FARO_OPTION_LABELS[option]}
                multiple
                size="small"
                renderOption={(option, { selected }) => (
                  <React.Fragment>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      className={classes.autocompleteCheckbox}
                      checked={selected}
                    />
                    {FARO_OPTION_LABELS[option]}
                  </React.Fragment>
                )}
                renderInput={(params) => <TextField {...params} label="Faro Options" margin="none" InputLabelProps={{
                  shrink: true
                }} />}
              />
            </div>
          </div>
          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.title}>Action Details</Typography>
            <div className={classes.sectionContent}>
              <TextField
                label="Action Name"
                disabled={autogenerateActionName}
                fullWidth
                required
                error={showErrors && (!actionName || actionNameSet.has(actionName))}
                helperText={showErrors ? (!actionName ? 'Action name is required' : actionNameSet.has(actionName) ? 'Action name already exists' : '') : ''}
                value={actionName}
                onChange={(event) => setActionName(event.target.value)}
                className={classes.textField}
                InputProps={{
                  endAdornment: <InputAdornment position="end">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={autogenerateActionName}
                          onChange={onAutogenerateActionNameChange}
                          name="autogenerateActionName"
                          color="default"
                        />
                      }
                      label="Autogenerate"
                    />
                  </InputAdornment>,
                }}
                InputLabelProps={{
                  shrink: true
                }} />
              <TextField
                fullWidth
                label="Action Group (optional)"
                value={actionGroup}
                onChange={(event) => setActionGroup(event.target.value)}
                InputLabelProps={{
                  shrink: true
                }}
              />
            </div>
          </div>
          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.title}>IAM Roles</Typography>
            <div className={classes.sectionContent}>
              <TextField
                value={assumeRoleArn}
                onChange={(event) => setAssumeRoleArn(event.target.value)}
                fullWidth
                label="Deploy Role to Assume"
                margin="normal"
                InputLabelProps={{
                  shrink: true
                }} />
              <TextField
                value={teardownAssumeRoleArn}
                onChange={(event) => setTeardownAssumeRoleArn(event.target.value)}
                fullWidth
                label="Teardown Role to Assume"
                margin="normal"
                InputLabelProps={{
                  shrink: true
                }} />
            </div>
          </div>
          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.title}>Dependencies</Typography>
            <div className={classes.sectionContent}>
              <Autocomplete
                value={dependencies}
                onChange={onDependenciesChange}
                options={deploymentActions.filter(item => item.ActionName !== action.ActionName).map(item => item.ActionName)}
                getOptionLabel={(option) => option}
                multiple
                size="small"
                renderOption={(option, { selected }) => (
                  <React.Fragment>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      className={classes.autocompleteCheckbox}
                      checked={selected}
                    />
                    {option}
                  </React.Fragment>
                )}
                renderInput={(params) => <TextField {...params} label="Action Dependencies" margin="normal" InputLabelProps={{
                  shrink: true
                }} />}
              />
            </div>
          </div>
          <div className={classes.section}>
            <div className={classNames(classes.title, classes.parameterSetSectionTitle)}>
              <Typography variant="subtitle1">Parameter Sets</Typography>
              {paramSets.length > 0 && <Button className={classes.addButton} onClick={onAddParamSet}><PlusIcon /> Add Parameter Set</Button>}
            </div>
            <div>
              {paramSets.length === 0 && <Button className={classes.addButton} onClick={onAddParamSet}><PlusIcon /> Add Parameter Set</Button>}
              {paramSets.length > 0 && <ActionParamSetListInput
                accountId={accountId}
                region={region}
                paramSets={paramSets}
                onParamSetsChange={newParamSets => setParamSets(newParamSets)}
              />}
            </div>
          </div>
          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.title}>Parameters</Typography>
            <div>
              {!showParameters && <Button className={classes.addButton} onClick={() => setShowParameters(true)}><PlusIcon /> Add Parameters</Button>}
              {showParameters && <FaroParameterListInput
                classes={{ tableHead: classes.parametersTableHead }}
                defaultParameters={parameters}
                hideKmsKeyArn
                onParametersChange={setParameters}
              />}
            </div>
          </div>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={onUpdate} color="primary" variant="contained">
          Update
        </Button>
      </DialogActions>
    </Dialog >
  );
};

export default DeploymentActionDialog;
